import { type GridRowId } from "@mui/x-data-grid-pro";
import { type BulkPropertyRow } from "../types";
import { type AcquirePolicy } from "./acquirePolicy";
import { isAllMattersField, isClosureExclusiveField } from "./checkboxFields";
import { isCheckboxDisabled } from "./checkboxAvailability";
import { isReuseCheckboxDisabled } from "./reuseAvailability";
import { isAcquiredForExclusion } from "./reuseMarks";

/**
 * 行データ・チェック状態で決まる不可の理由（ツールチップ用）。
 * リユース候補由来の不可は候補ごとの理由をツールチップの候補一覧側が出すため、ここでは扱わない
 */
export const getBulkCellDisabledReason = (
  field: string,
  row: BulkPropertyRow,
  closureCheckedIds: Set<GridRowId>,
  excludeAcquired: boolean
): string | null => {
  if (isCheckboxDisabled(field, row.realEstateType)) {
    return `${row.realEstateType}では取得できません`;
  }
  if (isClosureExclusiveField(field) && closureCheckedIds.has(row.id)) {
    return "閉鎖と同時には取得できません";
  }
  if (excludeAcquired && isAcquiredForExclusion(field, row)) {
    // 全部事項は4列まとめて取得済み扱いになるため、取得済みの候補が出ない列でも理由が分かるようにする
    return isAllMattersField(field)
      ? "未取得のみを取得する指定のため選択できません\n目録の組み合わせに関わらず、いずれかを取得済みなら全部事項は取得済み扱いになります"
      : "未取得のみを取得する指定のため選択できません";
  }
  return null;
};

/**
 * セルのチェック不可を合成して判定する。
 * 種別による不可（列×種別で固定）、閉鎖チェック中の行の排他（共担・信託以外は取得不可）、
 * 未取得のみを取得する指定、リユース候補による不可（取得条件・経過日数に依存）の
 * いずれかに当たれば不可。
 * この関数はセル描画・列の全選択・チェック状態の追従のすべてが参照するため、
 * ここに条件を足すと3者の挙動が同時にそろう
 */
export const isBulkCellDisabled = (
  field: string,
  row: BulkPropertyRow,
  policy: AcquirePolicy,
  pastDays: number,
  closureCheckedIds: Set<GridRowId>,
  now: Date,
  excludeAcquired = false
): boolean =>
  getBulkCellDisabledReason(field, row, closureCheckedIds, excludeAcquired) !=
    null ||
  isReuseCheckboxDisabled(field, row, policy, pastDays, now, excludeAcquired);
